/* =========================================================
   Sidebar — a route tree, not a flat list.

   Hierarchy: Organizaciones › Sites › Instructores, drawn with
   elbow connectors. Branches expand independently; the active
   node's ancestors auto-open. Collapses to a 64px icon rail
   (state persisted), and carries the same slate gradient as the
   login panel — no orange glow.
   ========================================================= */
import React, { type ReactNode } from 'react';
import type { Role } from '@/types';
import { ic, sized } from '@/components/Icons';
import { userOrgIds } from '@/data';
import { useI18n } from '@/i18n';
import { useAppStore } from '@/store/useAppStore';
import { useGo, useRoute, type ScreenId } from '@/hooks/useRoute';
import { readString, writeString } from '@/lib/storage';

// Todos los roles con acceso al portal. SITE_ADMIN faltaba, y sin él la
// navegación se le quedaba vacía en cuanto dejó de disfrazarse de SITE_MGR.
const ALL: Role[] = ['ETC_ADMIN', 'PARTNER_MGR', 'SITE_MGR', 'SITE_ADMIN'];

interface Node {
  id: ScreenId;
  label: string;
  icon: ReactNode;
  roles: Role[];
  children?: Node[];
}

/** Structure only — labels are resolved at render time. Keeping the shape
 *  static means the auto-expand effect never re-runs on a language change. */
const PARENT_OF: Partial<Record<ScreenId, ScreenId>> = {
  sites: 'org',
  instructors: 'sites',
};

const NAV = {
  line: 'rgba(255,255,255,0.12)',
  text: 'rgba(255,255,255,0.90)',
  muted: 'rgba(255,255,255,0.52)',
  activeBg: 'rgba(242,101,34,0.22)',
  activeFg: '#FBB089',
  activeIcon: 'var(--brand-primary)',
};

const LABEL_TRANS = 'opacity .16s ease, max-width .26s cubic-bezier(.4,0,.2,1)';

export function Sidebar() {
  const { lang, t } = useI18n();
  const go = useGo();
  const route = useRoute();
  const role = useAppStore((s) => s.role);
  const user = useAppStore((s) => s.user);

  const multiOrg = role === 'PARTNER_MGR' && userOrgIds(user).length > 1;

  const rawTree: Node[] = React.useMemo(
    () => [
      { id: 'dashboard', label: t('nav.dashboard'), icon: ic.dashboard, roles: ALL },
      {
        id: 'org',
        label: role === 'ETC_ADMIN' || multiOrg ? t('nav.orgs') : t('nav.org'),
        icon: ic.org,
        roles: ALL,
        children: [
          {
            id: 'sites',
            label: t('nav.sites'),
            icon: ic.site,
            roles: ALL,
            children: [{ id: 'instructors', label: t('nav.instructors'), icon: ic.instr, roles: ALL }],
          },
        ],
      },
      { id: 'users',   label: t('nav.users'),   icon: ic.users,   roles: ['ETC_ADMIN'] },
      { id: 'reports', label: t('nav.reports'), icon: ic.reports, roles: ['ETC_ADMIN'] },
      { id: 'admin',   label: t('nav.admin'),   icon: ic.admin,   roles: ['ETC_ADMIN'] },
    ],
    [t, role, multiOrg],
  );

  const filterTree = (nodes: Node[]): Node[] =>
    nodes
      .filter((n) => n.roles.includes(role))
      .map((n) => (n.children ? { ...n, children: filterTree(n.children) } : n));

  const tree = filterTree(rawTree);
  const flatten = (nodes: Node[]): Node[] => nodes.flatMap((n) => [n, ...(n.children ? flatten(n.children) : [])]);
  const flat = flatten(tree);

  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({ org: true });

  // auto-open every ancestor branch of the active route
  React.useEffect(() => {
    const opens: Record<string, boolean> = {};
    let cur = PARENT_OF[activeId(route.screen) as ScreenId];
    while (cur) {
      opens[cur] = true;
      cur = PARENT_OF[cur];
    }
    if (!Object.keys(opens).length) return;
    setExpanded((e) => {
      // bail out when nothing actually changes, or React re-renders forever
      if (Object.keys(opens).every((k) => e[k])) return e;
      return { ...e, ...opens };
    });
  }, [route.screen]);

  const [collapsed, setCollapsed] = React.useState(() => readString('etc.sidebar.collapsed') === '1');
  function toggle() {
    setCollapsed((c) => {
      const next = !c;
      writeString('etc.sidebar.collapsed', next ? '1' : '0');
      return next;
    });
  }

  const active = activeId(route.screen);

  return (
    <aside
      className="nav-dark"
      style={{
        width: collapsed ? 64 : 232, minWidth: collapsed ? 64 : 232,
        background: 'linear-gradient(162deg, #1B2330 0%, #2A3441 55%, #353F4F 100%)',
        borderRight: '1px solid ' + NAV.line,
        display: 'flex', flexDirection: 'column', padding: collapsed ? '14px 8px' : '14px 10px', gap: 2,
        height: 'calc(100vh - 56px)', position: 'sticky', top: 56, overflow: 'hidden',
        transition: 'width .26s cubic-bezier(.4,0,.2,1), min-width .26s cubic-bezier(.4,0,.2,1), padding .26s ease',
      }}
    >
      {/* brand backdrop — same geometry as the login left panel */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <svg style={{ position: 'absolute', left: -72, bottom: -72, opacity: 0.09 }} width="300" height="300" viewBox="0 0 300 300">
          <circle cx="150" cy="150" r="145" stroke="white" strokeWidth="1" fill="none" />
          <circle cx="150" cy="150" r="100" stroke="white" strokeWidth="1" fill="none" />
          <circle cx="150" cy="150" r="55" stroke="white" strokeWidth="1" fill="none" />
          <rect x="70" y="70" width="160" height="160" stroke="white" strokeWidth="1" fill="none" transform="rotate(45 150 150)" />
        </svg>
      </div>

      {/* header row: section label + collapse toggle */}
      <div
        style={{
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '4px 0 6px' : '6px 4px 6px 12px', minHeight: 30,
        }}
      >
        <span
          style={{
            fontSize: 10, color: NAV.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em',
            whiteSpace: 'nowrap', overflow: 'hidden',
            opacity: collapsed ? 0 : 1, maxWidth: collapsed ? 0 : 140, transition: LABEL_TRANS,
          }}
        >
          {lang === 'es' ? 'Navegación' : 'Navigation'}
        </span>
        <button
          type="button"
          onClick={toggle}
          className="btn ghost icon"
          aria-label={collapsed ? (lang === 'es' ? 'Expandir menú' : 'Expand menu') : lang === 'es' ? 'Contraer menú' : 'Collapse menu'}
          data-tip={collapsed ? (lang === 'es' ? 'Expandir' : 'Expand') : lang === 'es' ? 'Contraer' : 'Collapse'}
          style={{ padding: 7, borderRadius: 8, color: NAV.muted }}
        >
          <span style={{ display: 'inline-flex', transition: 'transform .26s cubic-bezier(.4,0,.2,1)', transform: collapsed ? 'rotate(180deg)' : 'none' }}>
            {sized(ic.chevL, 16)}
          </span>
        </button>
      </div>

      {collapsed
        ? flat.map((node) => {
            const isActive = active === node.id;
            return (
              <button
                key={node.id}
                onClick={() => go(node.id)}
                className="btn ghost"
                data-tip={node.label}
                style={{
                  position: 'relative', zIndex: 1,
                  justifyContent: 'center', padding: '10px 0', fontWeight: isActive ? 700 : 500, fontSize: 13,
                  background: isActive ? NAV.activeBg : 'transparent',
                  color: isActive ? NAV.activeFg : NAV.text,
                  border: 'none', borderRadius: 8,
                  transition: 'padding .26s cubic-bezier(.4,0,.2,1), background .15s ease',
                }}
              >
                <span style={{ color: isActive ? NAV.activeIcon : NAV.muted, display: 'inline-flex', flexShrink: 0 }}>{node.icon}</span>
              </button>
            );
          })
        : renderForest(tree, 0)}

      <div style={{ position: 'relative', zIndex: 1, marginTop: 'auto', padding: collapsed ? '12px 0 0' : '12px', borderTop: '1px solid ' + NAV.line, overflow: 'hidden' }}>
        <div
          style={{
            fontSize: 11, color: NAV.muted, fontWeight: 600, lineHeight: 1.4, whiteSpace: 'normal',
            opacity: collapsed ? 0 : 1, maxHeight: collapsed ? 0 : 80,
            transition: 'opacity .16s ease, max-height .26s ease',
          }}
        >
          <div style={{ color: NAV.text, fontWeight: 700, marginBottom: 2 }}>ETC Iberoamérica MCO</div>
          {lang === 'es' ? 'Innovamos para formar futuros brillantes.' : 'We innovate to shape brilliant futures.'}
        </div>
      </div>
    </aside>
  );

  function renderForest(nodes: Node[], depth: number): ReactNode {
    return nodes.map((node) => {
      const hasChildren = !!node.children?.length;
      const isOpen = !!expanded[node.id];
      const isActive = active === node.id;
      return (
        <React.Fragment key={node.id}>
          <button
            onClick={() => {
              go(node.id);
              if (hasChildren) setExpanded((e) => ({ ...e, [node.id]: true }));
            }}
            className="btn ghost"
            style={{
              position: 'relative', zIndex: 1,
              justifyContent: 'flex-start', alignItems: 'center',
              padding: '9px 11px', paddingLeft: 11 + depth * 18,
              fontWeight: isActive ? 700 : 500, fontSize: 13,
              background: isActive ? NAV.activeBg : 'transparent',
              color: isActive ? NAV.activeFg : NAV.text,
              border: 'none', borderRadius: 8, gap: 8,
              transition: 'background .15s ease',
            }}
          >
            {depth > 0 ? (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute', left: 11 + (depth - 1) * 18 + 4, top: -2, height: '52%', width: 9,
                  borderLeft: '1.5px solid rgba(255,255,255,0.18)', borderBottom: '1.5px solid rgba(255,255,255,0.18)',
                  borderBottomLeftRadius: 5,
                }}
              />
            ) : null}
            {hasChildren ? (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((x) => ({ ...x, [node.id]: !x[node.id] }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpanded((x) => ({ ...x, [node.id]: !x[node.id] }));
                  }
                }}
                aria-label={isOpen ? (lang === 'es' ? 'Contraer' : 'Collapse') : lang === 'es' ? 'Expandir' : 'Expand'}
                style={{ display: 'inline-flex', flexShrink: 0, color: NAV.muted, transform: isOpen ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .2s' }}
              >
                {sized(ic.chevD, 14)}
              </span>
            ) : (
              <span style={{ width: 14, flexShrink: 0 }} />
            )}
            <span style={{ color: isActive ? NAV.activeIcon : NAV.muted, display: 'inline-flex', flexShrink: 0 }}>{node.icon}</span>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0, textAlign: 'left' }}>
              {node.label}
            </span>
          </button>
          {hasChildren && isOpen ? renderForest(node.children!, depth + 1) : null}
        </React.Fragment>
      );
    });
  }
}

/** Detail screens highlight their parent nav node. */
function activeId(screen: string): string {
  if (screen === 'site') return 'sites';
  if (screen === 'instructorDetail') return 'instructors';
  return screen;
}
